'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { BlogPost } from '@/content/blog';

interface BlogListWithSearchProps {
  initialPosts: BlogPost[];
}

export default function BlogListWithSearch({ initialPosts }: BlogListWithSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPosts = initialPosts.filter((post) =>
    post.h1.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-brand-border pb-5">
        <div className="flex-1 max-w-md">
          <p className="section-label mb-2">Latest guides</p>
          <div className="relative flex items-center">
            <div className="absolute left-3.5 flex items-center pointer-events-none">
              <Icon name="MagnifyingGlassIcon" size={18} className="text-brand-muted" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search guides by title..."
              className="w-full pl-10 pr-10 py-2.5 bg-white border border-brand-border rounded-xl font-body text-[14px] text-brand-dark placeholder-brand-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-sm transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3.5 p-1 rounded-full text-brand-muted hover:text-brand-dark hover:bg-zinc-100 transition-colors focus:outline-none"
                aria-label="Clear search"
              >
                <Icon name="XMarkIcon" size={14} />
              </button>
            )}
          </div>
        </div>
        <p className="text-sm text-brand-muted sm:self-end">
          {searchQuery ? (
            <span>
              Found {filteredPosts.length} {filteredPosts.length === 1 ? 'guide' : 'guides'}
            </span>
          ) : (
            <span>{initialPosts.length} practical guides for everyday document work</span>
          )}
        </p>
      </div>

      {filteredPosts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 lg:gap-6">
          {filteredPosts.map((post) => (
            <article key={post.slug} className="tool-card p-5 sm:p-6 flex flex-col min-h-[280px]">
              <div className="flex flex-wrap items-center gap-2 text-xs text-brand-muted mb-3">
                <span>{post.readingTime}</span>
                <span aria-hidden="true">/</span>
                <span>{post.updatedAt}</span>
              </div>
              <h2 className="font-heading font-bold text-[20px] leading-snug text-brand-dark mb-3 line-clamp-2">
                <Link
                  href={`/blog/${post.slug}`}
                  className="hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                  title={post.h1}
                >
                  {post.h1}
                </Link>
              </h2>
              <p className="text-sm text-brand-mid leading-6 mb-5 grow shrink-0 line-clamp-3">
                {post.excerpt}
              </p>
              <div className="flex items-center justify-between gap-3 pt-4 border-t border-brand-border mt-auto">
                <Link
                  href={post.toolHref}
                  className="inline-flex items-center rounded-full bg-primary-light border border-primary-mid px-3 py-1 text-xs font-heading font-bold text-primary hover:text-primary-hover transition-colors truncate max-w-[60%]"
                  title={post.toolLabel}
                >
                  {post.toolLabel}
                </Link>
                <Link
                  href={`/blog/${post.slug}`}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-dark hover:text-primary transition-colors whitespace-nowrap shrink-0"
                >
                  Read guide
                  <Icon name="ArrowRightIcon" size={14} aria-hidden="true" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 px-4 bg-white border border-brand-border rounded-xl shadow-card text-center">
          <div className="w-14 h-14 rounded-full bg-primary-light flex items-center justify-center mb-4">
            <Icon name="DocumentMagnifyingGlassIcon" size={26} className="text-primary" />
          </div>
          <h3 className="font-heading font-bold text-lg text-brand-dark mb-1">No guides found</h3>
          <p className="text-brand-mid text-sm max-w-sm mb-5 leading-relaxed">
            We couldn't find any guides matching "{searchQuery}". Try using different terms or
            browse all topics.
          </p>
          <button
            onClick={() => setSearchQuery('')}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-heading font-bold text-white hover:bg-primary-hover transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Clear Search
          </button>
        </div>
      )}
    </div>
  );
}
